import type { FormField, FormFieldZodOptions } from '@cli-types/core-engine/schema.js'
import { assertSupportedFormFieldTree } from '@core-engine/schema/type-mappers/validation/assert-supported-form-field-tree.js'
import { optionArrayZodSchema } from './option-array-zod-schema.js'

export function formFieldToZodType(field: FormField, options: FormFieldZodOptions = {}): string {
  assertSupportedFormFieldTree(field)

  const label = field.label || field.name || ''
  const isRequired = field.required && !options.treatAsOptional

  switch (field.type) {
    case 'text': {
      let zodType = isRequired ? `z.string().min(1, '${label} is required')` : 'z.string()'
      if (field.minLength) {
        zodType += `.min(${field.minLength}, '${label} must be at least ${field.minLength} characters')`
      }
      if (field.maxLength) {
        zodType += `.max(${field.maxLength}, '${label} must be at most ${field.maxLength} characters')`
      }
      if (field.pattern) {
        zodType += `.regex(new RegExp('${field.pattern}'), 'Invalid ${label} format')`
      }
      return isRequired ? zodType : `${zodType}.optional()`
    }
    case 'textarea': {
      let zodType = isRequired ? `z.string().min(1, '${label} is required')` : 'z.string()'
      if (field.minLength) {
        zodType += `.min(${field.minLength}, '${label} must be at least ${field.minLength} characters')`
      }
      if (field.maxLength) {
        zodType += `.max(${field.maxLength}, '${label} must be at most ${field.maxLength} characters')`
      }
      return isRequired ? zodType : `${zodType}.optional()`
    }
    case 'email':
      return isRequired
        ? `z.string().min(1, '${label} is required').email('Please enter a valid email address')`
        : `z.string().optional().refine((val) => !val || val.trim() === '' || z.string().email().safeParse(val).success, { message: 'Please enter a valid email address' })`
    case 'phone': {
      const pattern =
        field.pattern || '^[+]?[(]?[0-9]{1,4}[)]?[-\\s\\.]?[(]?[0-9]{1,4}[)]?[-\\s\\.]?[0-9]{1,9}$'
      return isRequired
        ? `z.string().min(1, '${label} is required').regex(new RegExp('${pattern}'), 'Please enter a valid phone number')`
        : `z.string().optional().refine((val) => !val || val.trim() === '' || new RegExp('${pattern}').test(val), { message: 'Please enter a valid phone number' })`
    }
    case 'number': {
      let zodType = isRequired ? `z.number({ message: '${label} is required' })` : 'z.number()'
      if (field.min !== undefined) {
        zodType += `.min(${field.min}, '${label} must be at least ${field.min}')`
      }
      if (field.max !== undefined) {
        zodType += `.max(${field.max}, '${label} must be at most ${field.max}')`
      }
      return isRequired ? zodType : `${zodType}.optional()`
    }
    case 'url':
      return isRequired
        ? `z.string().min(1, '${label} is required').url('Please enter a valid URL')`
        : `z.string().optional().refine((val) => !val || val.trim() === '' || z.string().url().safeParse(val).success, { message: 'Please enter a valid URL' })`
    case 'date':
      return isRequired
        ? `z.string().min(1, '${label} is required')`
        : 'z.string().transform(val => val === "" ? undefined : val).optional()'
    case 'select':
    case 'radio':
      if (field.options && field.options.length > 0) {
        const values = field.options.map((opt) => `'${opt.value}'`).join(', ')
        return isRequired
          ? `z.enum([${values}], { message: '${label} is required' })`
          : `z.enum([${values}]).optional()`
      }
      return isRequired ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    case 'checkbox':
      if (field.options && field.options.length > 0) {
        return optionArrayZodSchema(field.options, label, isRequired)
      }
      return isRequired
        ? `z.boolean().refine(val => val === true, { message: '${label} is required' })`
        : 'z.boolean().optional()'
    case 'multiselect':
      if (field.options && field.options.length > 0) {
        return optionArrayZodSchema(field.options, label, isRequired)
      }
      return isRequired
        ? `z.array(z.string()).min(1, '${label} is required')`
        : 'z.array(z.string()).optional()'
    case 'timezone':
      return isRequired ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    case 'list':
      if (field.fields && field.fields.length > 0) {
        const nestedFields = field.fields
          .map((f) => `${f.name}: ${formFieldToZodType(f, options)}`)
          .join(', ')
        return isRequired
          ? `z.array(z.object({ ${nestedFields} })).min(1, '${label} is required')`
          : `z.array(z.object({ ${nestedFields} })).optional()`
      }
      return isRequired
        ? `z.array(z.string()).min(1, '${label} is required')`
        : 'z.array(z.string()).optional()'
    case 'file':
    case 'upload':
      return isRequired ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    default:
      return isRequired ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
  }
}
